import type { HandLandmark, HandSample, HandVoice, InstrumentControl, LadderRange, NoteEvent, VoicePreset } from '../domain/types'
import { countExtendedFingers, palmCentroid } from '../gesture/fingerCount'

export const PENTATONIC_INTERVALS = [0, 3, 5, 7, 10, 12, 15, 17, 19, 22, 24] as const

export const VOICE_REGISTERS: Record<VoicePreset, number> = {
  harp: 48,
  'music-box': 72,
  piano: 48,
  'glass-bow': 60,
  'breath-choir': 48,
  aurora: 60
}

// The comfortable vertical band of the camera frame that spans the note ladder.
export const LADDER_TOP = 0.15
export const LADDER_BOTTOM = 0.85

const SLOT_COUNT = 2
const ACCENT_TAU_MS = 260
// Depth (hand size relative to its resting size) → volume. A hand pushed ~20%
// closer reads about 20% larger and plays near full volume; pulled back it fades.
const DEPTH_GAIN = 3.2
const DEPTH_NEUTRAL = 0.55
const BASELINE_ADAPT = 0.012
const BASELINE_WINDOW = 0.12

interface SlotState {
  noteIndex: number | null
  gate: boolean
  depth: number
  spanBaseline: number | null
  accent: number
  lastY: number | null
  lastTimestampMs: number | null
  emittedGate: boolean
  emittedMidi: number | null
  emittedIndex: number | null
}

export interface UpdateOptions {
  frozen?: ReadonlySet<number>
  // The visible vertical band of the camera frame; a cover-cropped camera
  // narrows this so every note stays reachable on screen.
  ladder?: LadderRange
}

// Both hands are equal voices. The index fingertip points at the note (higher
// finger, higher note), an open hand plays, a fist rests, pushing the hand
// toward the camera swells the volume, and horizontal position tints the tone
// from warm/left to bright/right while panning it the same way.
export class DualHandMapper {
  private preset: VoicePreset
  private slots: SlotState[] = [emptySlot(), emptySlot()]

  constructor(preset: VoicePreset) {
    this.preset = preset
  }

  setPreset(preset: VoicePreset): void {
    this.preset = preset
  }

  update(hands: readonly HandSample[], timestampMs: number, options: UpdateOptions = {}): InstrumentControl {
    const ladder = options.ladder ?? { top: LADDER_TOP, bottom: LADDER_BOTTOM }
    const xs: number[] = []
    const active: boolean[] = []
    for (let slotIndex = 0; slotIndex < SLOT_COUNT; slotIndex += 1) {
      const slot = this.slots[slotIndex]!
      const hand = hands.find((candidate) => (candidate.slot ?? 0) === slotIndex && candidate.confidence >= 0.55 && candidate.landmarks.length >= 21) ?? null
      if (!hand) {
        slot.gate = false
        slot.lastY = null
        slot.lastTimestampMs = null
        xs.push(0.5)
        active.push(false)
        continue
      }
      const palm = palmCentroid(hand)
      const tip = hand.landmarks[8]!
      const extended = countExtendedFingers(hand)
      if (extended >= 3) slot.gate = true
      else if (extended <= 1) slot.gate = false
      this.trackDepth(slot, palmSpan(hand.landmarks))
      this.trackMotion(slot, tip.y, timestampMs)
      if (!options.frozen?.has(slotIndex) || slot.noteIndex === null) {
        slot.noteIndex = updateNoteIndex(slot.noteIndex, heightPosition(tip.y, ladder))
      }
      xs.push(clamp01(palm.x))
      active.push(true)
    }
    return this.compose(xs, active, ladder, timestampMs, hands)
  }

  fallback(x: number, y: number, gate: boolean, timestampMs: number): InstrumentControl {
    const ladder = { top: LADDER_TOP, bottom: LADDER_BOTTOM }
    const slot = this.slots[0]!
    slot.gate = gate
    slot.depth = 0.8
    this.trackMotion(slot, clamp01(y), timestampMs)
    slot.noteIndex = updateNoteIndex(slot.noteIndex, heightPosition(clamp01(y), ladder))
    this.slots[1]!.gate = false
    return this.compose([clamp01(x), 0.5], [true, false], ladder, timestampMs, [])
  }

  reset(): void {
    this.slots = [emptySlot(), emptySlot()]
  }

  private trackDepth(slot: SlotState, span: number): void {
    if (slot.spanBaseline === null) slot.spanBaseline = span
    const ratio = span / Math.max(0.001, slot.spanBaseline)
    // Only re-centre while the hand rests near neutral, so a sustained push stays loud.
    if (Math.abs(ratio - 1) < BASELINE_WINDOW) slot.spanBaseline += (span - slot.spanBaseline) * BASELINE_ADAPT
    slot.depth = clamp01(DEPTH_NEUTRAL + (ratio - 1) * DEPTH_GAIN)
  }

  private trackMotion(slot: SlotState, y: number, timestampMs: number): void {
    if (slot.lastY !== null && slot.lastTimestampMs !== null) {
      const dt = Math.max(1, timestampMs - slot.lastTimestampMs)
      slot.accent *= Math.exp(-dt / ACCENT_TAU_MS)
      const speed = Math.abs(y - slot.lastY) / dt * 1_000
      slot.accent = Math.min(1, slot.accent + clamp01(speed / 2.2) * 0.6)
    }
    slot.lastY = y
    slot.lastTimestampMs = timestampMs
  }

  private compose(xs: number[], active: boolean[], ladder: LadderRange, timestampMs: number, hands: readonly HandSample[]): InstrumentControl {
    const voices = this.slots.map((slot, index) => {
      const hand = hands.find((candidate) => (candidate.slot ?? 0) === index) ?? null
      return this.voiceFor(slot, index, xs[index]!, active[index]!, hand?.handedness ?? null)
    }) as [HandVoice, HandVoice]
    const events: NoteEvent[] = []
    voices.forEach((voice, index) => {
      const slot = this.slots[index]!
      if (voice.gate && !slot.emittedGate) {
        events.push(noteEvent('on', index, voice, voice.targetMidi))
      } else if (voice.gate && slot.emittedIndex !== null && slot.emittedIndex !== voice.noteIndex) {
        // A fast sweep can cross several notes in one frame: play every note it passed.
        const step = voice.noteIndex > slot.emittedIndex ? 1 : -1
        for (let noteIndex = slot.emittedIndex + step; noteIndex !== voice.noteIndex + step; noteIndex += step) {
          events.push(noteEvent('change', index, voice, VOICE_REGISTERS[this.preset] + PENTATONIC_INTERVALS[noteIndex]!))
        }
      } else if (!voice.gate && slot.emittedGate) {
        events.push(noteEvent('off', index, voice, slot.emittedMidi ?? voice.targetMidi))
      }
      slot.emittedGate = voice.gate
      slot.emittedMidi = voice.gate ? voice.targetMidi : null
      slot.emittedIndex = voice.gate ? voice.noteIndex : null
    })
    return { voices, events, ambience: 0.5, ladder, timestampMs }
  }

  private voiceFor(slot: SlotState, index: number, x: number, active: boolean, handedness: HandVoice['handedness']): HandVoice {
    const noteIndex = slot.noteIndex ?? 0
    const targetMidi = VOICE_REGISTERS[this.preset] + PENTATONIC_INTERVALS[noteIndex]!
    const gate = active && slot.gate
    const level = gate ? Math.min(1, (0.25 + 0.75 * slot.depth) * (0.85 + 0.3 * slot.accent)) : 0
    return {
      slot: index,
      active,
      gate,
      targetMidi,
      pitchHz: midiToHz(targetMidi),
      noteIndex,
      level,
      timbre: x,
      pan: Math.max(-0.8, Math.min(0.8, (x - 0.5) * 1.5)),
      handedness
    }
  }
}

function noteEvent(type: NoteEvent['type'], slot: number, voice: HandVoice, midi: number): NoteEvent {
  return { type, slot, midi, velocity: Math.max(0.2, voice.level || 0.6), timbre: voice.timbre, pan: voice.pan }
}

// Apparent hand size: the larger of palm width (index to pinky knuckle) and
// palm length (wrist to middle knuckle), so tilting the hand does not read as depth.
export function palmSpan(landmarks: HandLandmark[]): number {
  const width = Math.hypot(landmarks[5]!.x - landmarks[17]!.x, landmarks[5]!.y - landmarks[17]!.y)
  const length = Math.hypot(landmarks[0]!.x - landmarks[9]!.x, landmarks[0]!.y - landmarks[9]!.y)
  return Math.max(width, length)
}

function emptySlot(): SlotState {
  return { noteIndex: null, gate: false, depth: DEPTH_NEUTRAL, spanBaseline: null, accent: 0, lastY: null, lastTimestampMs: null, emittedGate: false, emittedMidi: null, emittedIndex: null }
}

// Convert a frame y (0 = top) into a 0..1 ladder position (0 = lowest note).
function heightPosition(y: number, ladder: LadderRange): number {
  return clamp01((ladder.bottom - y) / Math.max(0.05, ladder.bottom - ladder.top))
}

function updateNoteIndex(current: number | null, position: number): number {
  const candidate = Math.round(clamp01(position) * (PENTATONIC_INTERVALS.length - 1))
  if (current === null || Math.abs(candidate - current) > 1) return candidate
  const zone = 1 / (PENTATONIC_INTERVALS.length - 1)
  const hysteresis = zone * 0.15
  if (candidate > current && position >= (current + 0.5) * zone + hysteresis) return candidate
  if (candidate < current && position <= (current - 0.5) * zone - hysteresis) return candidate
  return current
}

function midiToHz(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12)
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}
